import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsEquipmentSystem } from './useData';
import {
  pmsCriticalityField,
  pmsDepartmentField,
  pmsLookupSelect,
  pmsRecordStatusField,
  pmsVesselField} from '../shared/pmsFormFields';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';

export const moduleConfig = {
  module: {
    title: "Equipment systems",
    permissionModuleKey: 'pms_hierarchy',
    route: {
      list: '/pms/pms-equipment-systems',
      new: '/pms/pms-equipment-systems/new',
      detail: (id: number) => `/pms/pms-equipment-systems/${id}`,
      edit: (id: number) => `/pms/pms-equipment-systems/${id}/edit`},
    api: { list: '/pms-equipment-systems', detail: (id: number) => `/pms-equipment-systems/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsEquipmentSystem[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-equipment-systems/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsEquipmentSystem) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsEquipmentSystem } = await import('../../../services/mutations');
      await deletePmsEquipmentSystem(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "Equipment systems",
    getTitle: (item: PmsEquipmentSystem) => item.name ?? `#${item.id}`},
  form: {
    new: { title: 'New equipment system', submitText: 'Create' },
    edit: { title: 'Edit equipment system', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsEquipmentSystem>) => {
      const { createPmsEquipmentSystem } = await import('../../../services/mutations');
      return createPmsEquipmentSystem(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<PmsEquipmentSystem>) => {
      const { updatePmsEquipmentSystem } = await import('../../../services/mutations');
      return updatePmsEquipmentSystem(id, data as Record<string, unknown>);
    }},
  fields: [
    {
      ...pmsVesselField(true),
      helperText: 'Systems sit under a vessel department in the PMS hierarchy',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('departmentId', '');
        setFieldValue('parentSystemId', '');
      }},
    {
      ...pmsDepartmentField(true),
      helperText: 'Owning department on this vessel (Engine, Deck, …)',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('parentSystemId', '');
      }},
    {
      ...pmsLookupSelect({
        name: 'parentSystemId',
        label: 'Parent system',
        apiEndpoint: '/pms-equipment-systems',
        required: false,
        clearable: true,
        placeholder: 'None — top-level system',
        optionsDependOnFields: ['departmentId', 'vesselId'],
        optionsEndpointFromFormData: () => '/pms-equipment-systems',
        filterDynamicOptionItem: (item, fd) => {
          if (fd.id && Number(item.id) === Number(fd.id)) return false;
          if (fd.departmentId && Number(item.departmentId) !== Number(fd.departmentId)) return false;
          if (fd.vesselId && Number(item.vesselId) !== Number(fd.vesselId)) return false;
          return true;
        }}),
      helperText: 'Optional. Nest under another system in the same department'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Main propulsion system',
      validation: { required: true }},
    {
      name: 'systemType',
      label: 'System type',
      type: 'text',
      placeholder: 'e.g. Propulsion, HVAC, Power',
      helperText: 'Free-text category for filtering and reports'},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Optional notes about this system’s scope'},
    {
      ...pmsCriticalityField(true),
      helperText: 'How critical this system is to vessel operations'},
    {
      ...pmsRecordStatusField(true),
      helperText: 'Lifecycle state of this system record'},
    {
      name: 'displayOrder',
      label: 'Display order',
      type: 'number',
      min: 0,
      placeholder: '0',
      helperText: 'Lower numbers appear first in the hierarchy tree'},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'System is active'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'placement',
        title: 'Placement',
        description: 'Where this system lives in the vessel hierarchy.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'departmentId', 'parentSystemId'],
        fieldSpans: {
          vesselId: 'md:col-span-4',
          departmentId: 'md:col-span-4',
          parentSystemId: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'identity',
        title: 'System',
        description: 'Name and type as they appear under the department.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['name', 'systemType', 'description', 'criticalityId'],
        fieldSpans: {
          name: 'md:col-span-8',
          systemType: 'md:col-span-4',
          description: 'md:col-span-12',
          criticalityId: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'status',
        title: 'Status & ordering',
        description: 'Record lifecycle and sort position in lists and the tree.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['recordStatusId', 'displayOrder', 'isActive'],
        fieldSpans: {
          recordStatusId: 'md:col-span-5',
          displayOrder: 'md:col-span-3',
          isActive: 'md:col-span-4'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const codeName = (
  rel: { code?: string | null; name?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsEquipmentSystem) => {
  const v = item.vessel;
  if (!v && item.vesselId == null) return '-';
  const name = v?.vesselName ?? v?.name;
  return name ?? (item.vesselId != null ? `#${item.vesselId}` : '-');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsEquipmentSystem>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsEquipmentSystem, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      { label: 'Department', value: codeName(item.department, item.departmentId) },
      { label: 'Criticality', value: codeName(item.criticality, item.criticalityId) },
      { label: 'Record status', value: codeName(item.recordStatus, item.recordStatusId) },
      { label: 'Parent system', value: codeName(item.parentSystem, item.parentSystemId) },
      { label: 'Name', value: item.name != null ? String(item.name) : '' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'System type', value: item.systemType != null ? String(item.systemType) : '-' },
      { label: 'Display order', value: item.displayOrder != null ? String(item.displayOrder) : '-' },
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsEquipmentSystem> => ({
  vesselId: undefined,
  departmentId: undefined,
  criticalityId: undefined,
  recordStatusId: undefined,
  parentSystemId: undefined,
  name: '',
  description: '',
  systemType: '',
  displayOrder: undefined,
  isActive: true});
export const mapItemToFormData = (item: PmsEquipmentSystem): Partial<PmsEquipmentSystem> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  departmentId: item.departmentId != null ? Number(item.departmentId) : undefined,
  criticalityId: item.criticalityId != null ? Number(item.criticalityId) : undefined,
  recordStatusId: item.recordStatusId != null ? Number(item.recordStatusId) : undefined,
  parentSystemId: item.parentSystemId != null ? Number(item.parentSystemId) : undefined,
  name: item.name ?? '',
  description: item.description ?? '',
  systemType: item.systemType ?? '',
  displayOrder: item.displayOrder != null ? Number(item.displayOrder) : undefined,
  isActive: item.isActive ?? true});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
